/**
 * DueBadge: an open assignment's due date with its state (plan
 * docs/plans/lms-plan.md 7.1 "due date with the overdue or due-soon state";
 * section 13's colors: overdue red, a warning amber, primary for an ordinary
 * assignment; each with its word).
 *
 * The state comes from the server when it sent one (GET /me/assignments
 * due_state, WP-31 mine.js: 'overdue' past due_at, 'due_soon' within 7 days,
 * the 9.4 summary's window, else 'upcoming'); dueStateOf is the same rule for
 * a caller that has only the date.
 *
 * formatDay is the module's calendar-date format (d MMM yyyy, Appendix F's
 * date format, the player's DoneStage format), exported for the Home and
 * Records screens.
 */
import { format } from 'date-fns';
import { Pill } from './StatusPill';
import type { PillTone } from './StatusPill';

export type DueState = 'overdue' | 'due_soon' | 'upcoming';

/** 9.4 and WP-31 mine.js DUE_SOON_DAYS: "due soon" is within 7 days. */
export const DUE_SOON_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/** d MMM yyyy, or '' for a missing or unreadable date. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '';
  // A plain calendar date (YYYY-MM-DD) is shown as that day, not shifted by the time zone.
  const plain = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = plain ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'd MMM yyyy');
}

/** The due state of an open assignment's due instant (the WP-31 mine.js rule). */
export function dueStateOf(dueAt: string | null | undefined, nowMs: number = Date.now()): DueState | null {
  if (!dueAt) return null;
  const due = Date.parse(dueAt);
  if (!Number.isFinite(due)) return null;
  if (nowMs > due) return 'overdue';
  if (due - nowMs <= DUE_SOON_DAYS * DAY_MS) return 'due_soon';
  return 'upcoming';
}

const STATE_PILL: Readonly<Record<DueState, { tone: PillTone; word: string }>> = {
  overdue: { tone: 'red', word: 'Overdue' },
  due_soon: { tone: 'amber', word: 'Due soon' },
  upcoming: { tone: 'primary', word: 'Upcoming' },
};

export interface DueBadgeProps {
  dueAt: string | null | undefined;
  /** The server's due_state; computed from dueAt when absent. */
  state?: DueState | null;
  'data-testid'?: string;
}

export function DueBadge({ dueAt, state, 'data-testid': testId }: DueBadgeProps) {
  const resolved = state ?? dueStateOf(dueAt);
  const day = formatDay(dueAt);
  const pill = resolved ? STATE_PILL[resolved] : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={testId} data-state={resolved ?? ''}>
      {pill ? <Pill tone={pill.tone}>{pill.word}</Pill> : null}
      {day ? <span className="whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">{`Due ${day}`}</span> : null}
    </span>
  );
}

export default DueBadge;
